// src/App.jsx

import React, { useState, useEffect } from 'react';

// Layout Components
import ModernHeader from './components/layout/ModernHeader';
import FloatingNav from './components/layout/FloatingNav';
import FAB from './components/layout/FAB';

// Home Components
import HeroSection from './components/home/HeroSection';
import DictionaryCarousel from './components/home/DictionaryCarousel';

// Analyze Components
import FullHeightInput from './components/analyze/FullHeightInput';
import BottomActionBar from './components/analyze/BottomActionBar';
import ResultTabs from './components/analyze/ResultTabs';
import LoadingState from './components/common/LoadingState';

// Dictionary Components
import CategoryGrid from './components/dictionary/CategoryGrid';
import DictionaryList from './components/dictionary/DictionaryList';

// Settings Components
import ProviderSettings from './components/settings/ProviderSettings';
import UserProfileSettings from './components/settings/UserProfileSettings';

// Modals
import ApiKeyModal from './components/modals/ApiKeyModal';

// Common Components
import ErrorState from './components/common/ErrorState';

// Hooks
import { useApiKeys } from './hooks/useApiKeys';
import { useAnalysis } from './hooks/useAnalysis';
import { useUserProfile } from './hooks/useUserProfile';

// Constants
import { PESANTREN_DEMO_TEXT } from './constants/demoData';

export default function App() {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [activeTab, setActiveTab]             = useState('home');
  const [activeResultTab, setActiveResultTab] = useState('nahwu');
  const [inputText, setInputText]             = useState('');
  const [showKeyModal, setShowKeyModal]       = useState(false);

  // ============================================
  // HOOKS
  // ============================================
  const { activeProvider, switchProvider }           = useApiKeys();
  const { profile, saveProfile, clearProfile }       = useUserProfile();
  const {
    loading,
    errorMsg,
    analysisResult,
    analyze,
    setAnalysisResult,
  } = useAnalysis(activeProvider);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    document.body.classList.add('preload');
    const timer = setTimeout(() => {
      document.body.classList.remove('preload');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (activeTab === 'analyze') {
      const mainEl = document.getElementById('main-scroll');
      if (mainEl) mainEl.scrollTop = 0;
    }
  }, [activeTab]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleStartAnalysis = () => setActiveTab('analyze');

  const handleAnalyze = () => {
    if (!inputText.trim()) return;
    analyze(inputText);
  };

  const handleUseExample = () => setInputText(PESANTREN_DEMO_TEXT);

  const handleClear = () => {
    setInputText('');
    setAnalysisResult(null);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputText(text);
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      alert('❌ Gagal membaca clipboard. Mohon paste manual (Ctrl/Cmd + V)');
    }
  };

  const handleProviderChange = (provider) => switchProvider(provider);

  const handleSaveProfile = (newName) => {
    const saved = saveProfile(newName);
    return saved !== null;
  };

  const handleCategoryClick = (categoryId) => {
    console.log('Category clicked:', categoryId);
    setActiveTab('dictionary');
  };

  const handleTermClick = (termId) => {
    console.log('Term clicked:', termId);
    setActiveTab('dictionary');
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  // ── Tab Beranda ──────────────────────────────
  const renderHomeTab = () => (
    <div className="pt-4 pb-6 space-y-5 animate-fade-in">
      <HeroSection
        userName={profile.name}
        onStartAnalysis={handleStartAnalysis}
      />

      <DictionaryCarousel
        onViewAll={() => setActiveTab('dictionary')}
        onTermClick={handleTermClick}
      />
    </div>
  );

  // ── Tab Analisis ─────────────────────────────
  const renderAnalyzeTab = () => {
    if (analysisResult && !loading) {
      return (
        <div className="pt-4 pb-6 animate-fade-in">
          <ResultTabs
            activeTab={activeResultTab}
            onTabChange={setActiveResultTab}
            data={analysisResult}
          />
          <div className="px-4 mt-4">
            <button
              onClick={handleClear}
              className="
                w-full flex items-center justify-center gap-2
                bg-white dark:bg-slate-800
                border-2 border-stone-200 dark:border-slate-600
                hover:border-green-400 hover:bg-green-50
                dark:hover:border-green-600 dark:hover:bg-green-900/10
                text-stone-600 dark:text-slate-300
                hover:text-green-700 dark:hover:text-green-400
                font-bold text-sm
                py-3.5 rounded-3xl
                transition-all active:scale-95 shadow-soft
              "
            >
              ← Analisis Teks Baru
            </button>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="pt-4 animate-fade-in">
          <LoadingState type="analysis" />
        </div>
      );
    }

    if (errorMsg) {
      return (
        <div className="pt-4 animate-fade-in">
          <ErrorState
            type="api"
            message={errorMsg}
            onRetry={handleAnalyze}
            onSettings={() => setShowKeyModal(true)}
          />
        </div>
      );
    }

    return (
      <div className="pt-4 pb-2 animate-fade-in">
        <FullHeightInput
          value={inputText}
          onChange={setInputText}
          placeholder="أدخل النص العربي هنا..."
          autoFocus={true}
        />
        <BottomActionBar
          onPaste={handlePaste}
          onExample={handleUseExample}
          onAnalyze={handleAnalyze}
          loading={loading}
          disabled={!inputText.trim()}
        />
      </div>
    );
  };

  // ── Tab Kamus ───────────────────────────────
  const renderDictionaryTab = () => (
    <div className="pt-4 pb-6 space-y-4 animate-fade-in">
      <CategoryGrid onCategoryClick={handleCategoryClick} />
      <DictionaryList />
    </div>
  );

  // ── Tab Pengaturan ───────────────────────────
  const renderSettingsTab = () => (
    <div className="pt-4 pb-6 px-4 animate-fade-in space-y-4">
      <UserProfileSettings
        profile={profile}
        onSave={handleSaveProfile}
        onClear={clearProfile}
      />
      <ProviderSettings
        activeProvider={activeProvider}
        onProviderChange={handleProviderChange}
      />
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-[#E2E8F0] flex justify-center sm:items-center sm:p-6">
      <div
        className="
          relative w-full max-w-md
          bg-[#F0F4F8] dark:bg-[#0f172a]
          h-[100dvh] sm:h-[92vh]
          sm:rounded-[40px]
          sm:shadow-[0_32px_64px_rgba(0,0,0,0.15)]
          sm:border-[10px] sm:border-slate-800/10
          flex flex-col
          overflow-hidden
        "
      >
        {/* ── Header (Tanpa userName) ──────────────────── */}
        <ModernHeader onSettingsClick={() => setShowKeyModal(true)} />

        {/* ── Main Scrollable Area ───────────────────── */}
        <main
          id="main-scroll"
          className="flex-1 overflow-y-auto overflow-x-hidden relative"
          style={{
            paddingBottom: 'calc(140px + env(safe-area-inset-bottom))',
          }}
        >
          {activeTab === 'home'       && renderHomeTab()}
          {activeTab === 'analyze'    && renderAnalyzeTab()}
          {activeTab === 'dictionary' && renderDictionaryTab()}
          {activeTab === 'settings'   && renderSettingsTab()}
        </main>

        {/* ── FAB ────────────────────────────────────── */}
        {activeTab !== 'analyze' && (
          <FAB
            onPaste={handlePaste}
            onCamera={() => alert('📸 Camera OCR coming soon!')}
            onVoice={() => alert('🎤 Voice Input coming soon!')}
          />
        )}

        {/* ── Floating Navigation ────────────────────── */}
        <FloatingNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Modal di luar shell */}
      <ApiKeyModal
        isOpen={showKeyModal}
        onClose={() => setShowKeyModal(false)}
        initialProvider={activeProvider}
        geminiApiKey=""
        cohereApiKey=""
        onSave={() => setShowKeyModal(false)}
        onDemo={() => setShowKeyModal(false)}
      />
    </div>
  );
}